import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const fmtUsd = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

/**
 * Slide-out drawer that shows the full producer listing (form fields,
 * uploaded documents) plus admin/compliance actions (assign officer,
 * request info, approve, reject). Approve publishes credits to the
 * marketplace priced at the current UCS Index spot USD.
 *
 * Reused by /admin/producers and /compliance.
 */
export default function ProducerReviewInlineDrawer({ listing, onClose, onChanged }) {
  const [note, setNote] = useState("");
  const [officerEmail, setOfficerEmail] = useState(listing.assigned_officer_email || "");
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(listing);
  const activeDocs = (current.documents || []).filter((d) => !d.is_deleted);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const act = async (endpoint) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/producers/listings/${current.id}/${endpoint}`, { note });
      setCurrent(data);
      toast.success("Updated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    if (!officerEmail) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/producers/listings/${current.id}/assign`, { officer_email: officerEmail });
      setCurrent(data);
      toast.success(`Assigned to ${officerEmail}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-ink/60" onClick={onClose} data-testid="review-drawer">
      <div className="w-full max-w-2xl bg-bg overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-bg border-b border-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-tag">Review · {current.status.replace("_", " ")}</div>
            <div className="font-serif text-xl">{current.name}</div>
          </div>
          <button onClick={onClose} className="px-3 py-2 text-[11px] font-mono uppercase border border-line hover:border-ink" data-testid="review-close">
            ✕ Close
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Producer" v={`${current.producer_name} · ${current.producer_email}`} />
            <Info label="Region" v={current.region} />
            <Info label="Methodology" v={current.methodology} />
            <Info label="Vintage" v={current.vintage} />
            <Info label="Volume" v={`${Number(current.tons_available).toLocaleString()} tCO₂`} />
            <Info label="Producer ask" v={`${fmtUsd(current.ask_price_usd_per_ton)}/ton`} />
            <Info label="Hectares" v={current.hectares || "—"} />
            <Info label="Geo" v={current.latitude ? `${current.latitude}, ${current.longitude}` : "—"} />
            <Info label="BMV ISIN (pre-reg)" v={current.bmv_isin_pre || "—"} />
            <Info label="Property" v={current.property_name || "—"} />
          </div>
          {current.description && (
            <div>
              <div className="label-tag mb-1">Description</div>
              <div className="text-sm text-ink whitespace-pre-wrap">{current.description}</div>
            </div>
          )}

          <div className="border border-line bg-bg/40 p-3 text-[11px] font-mono text-ink2 leading-relaxed">
            <span className="text-ink">Note:</span> once approved, the credits enter marketplace inventory priced at the
            current UCS Index spot USD (not the producer ask). Producer&rsquo;s ask is retained on the underlying listing
            for provenance.
          </div>

          {/* Documents */}
          <div>
            <div className="label-tag mb-3">Documents ({activeDocs.length})</div>
            {activeDocs.length === 0 && <div className="text-ink2 text-sm italic">No documents attached.</div>}
            <div className="space-y-2">
              {activeDocs.map((d) => (
                <a
                  key={d.id}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/producer/documents/${d.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between border border-line bg-surface p-3 text-sm hover:border-ink"
                  data-testid={`review-doc-${d.id}`}
                >
                  <div>
                    <div>{d.original_filename}</div>
                    <div className="text-[11px] font-mono text-ink2 mt-1">
                      {d.doc_type.replace("_", " ")} · {(d.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                  <span className="text-[11px] font-mono text-ink2">Open →</span>
                </a>
              ))}
            </div>
          </div>

          {/* Assign officer */}
          <div className="border-t border-line pt-6">
            <div className="label-tag mb-2">Assigned officer</div>
            <div className="flex gap-2">
              <input
                className="input-field flex-1"
                placeholder="officer@greenbelt.io"
                value={officerEmail}
                onChange={(e) => setOfficerEmail(e.target.value)}
                data-testid="review-officer-email"
              />
              <button onClick={assign} disabled={busy || !officerEmail} className="px-4 py-2 border border-ink text-[11px] font-mono uppercase hover:bg-ink hover:text-white disabled:opacity-50" data-testid="review-assign">
                Assign
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="border-t border-line pt-6 space-y-3">
            <div className="label-tag">Review actions</div>
            <textarea
              className="input-field min-h-[80px]"
              placeholder="Reviewer note (visible to the producer)…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="review-note"
            />
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => act("approve")}
                disabled={busy || current.status === "approved"}
                data-testid="review-approve"
                className="px-4 py-3 bg-leaf text-white text-[11px] font-mono uppercase hover:bg-leaf/80 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => act("request-info")}
                disabled={busy}
                data-testid="review-request-info"
                className="px-4 py-3 border border-ink text-[11px] font-mono uppercase hover:bg-ink hover:text-white disabled:opacity-50"
              >
                Request info
              </button>
              <button
                onClick={() => act("reject")}
                disabled={busy}
                data-testid="review-reject"
                className="px-4 py-3 bg-danger text-white text-[11px] font-mono uppercase hover:bg-danger/80 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>

          {/* History */}
          {(current.status_history || []).length > 0 && (
            <div className="border-t border-line pt-6">
              <div className="label-tag mb-3">Status history</div>
              <div className="space-y-2 text-[12px] font-mono">
                {[...current.status_history].reverse().map((h) => (
                  <div key={`${h.status}-${h.changed_at}`} className="flex justify-between gap-3 border-l-2 border-line pl-3">
                    <div>
                      <div className="text-ink uppercase">{h.status.replace("_", " ")}</div>
                      {h.note && <div className="text-ink2">{h.note}</div>}
                    </div>
                    <div className="text-ink2 text-right">
                      <div>{new Date(h.changed_at).toLocaleString()}</div>
                      <div className="text-[10px]">{h.changed_by_email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, v }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink2">{label}</div>
      <div className="text-ink mt-1">{v}</div>
    </div>
  );
}
